'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { DURATION, EASE_OUT_STRONG } from '@/lib/motion-utils';
import { PlaceholdersAndVanishInput } from '@/components/ui/placeholders-and-vanish-input';
import { SectionHeader } from '@/components/SectionHeader';
import { MailThemedIcon } from '@/components/icons';
import { useWebHaptics } from 'web-haptics/react';
import { useAnalytics } from '@/hooks/useAnalytics';

// Hoisted to module level to avoid recreation on each render
const PLACEHOLDERS = ['Enter email'];
export function Newsletter() {
  const $ = _c(15);
  const form = useNewsletterForm();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SectionHeader icon={MailThemedIcon} label="Join my mailing list" />;
    t1 = <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">Get notified when I ship new projects and share thoughts on building.</p>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== form.handleAnimationComplete || $[3] !== form.handleSubmit || $[4] !== form.showSuccess || $[5] !== form.updateNewsletterEmail) {
    t2 = <PlaceholdersAndVanishInput placeholders={PLACEHOLDERS} onChange={form.updateNewsletterEmail} onSubmit={form.handleSubmit} isSuccess={form.showSuccess} onAnimationComplete={form.handleAnimationComplete} />;
    $[2] = form.handleAnimationComplete;
    $[3] = form.handleSubmit;
    $[4] = form.showSuccess;
    $[5] = form.updateNewsletterEmail;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== form.status.message || $[8] !== form.status.s) {
    t3 = form.status.s === "error" && <m.p initial={{
      opacity: 0,
      y: -4
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -4,
      transition: {
        duration: DURATION.exit
      }
    }} transition={{
      duration: DURATION.enter,
      ease: EASE_OUT_STRONG
    }} className="mt-2 text-sm text-red-600 dark:text-red-400">{form.status.message}</m.p>;
    $[7] = form.status.message;
    $[8] = form.status.s;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <AnimatePresence>{t3}</AnimatePresence>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <div className="mt-8 rounded-2xl border border-zinc-100 p-6 dark:border-zinc-700/40">{t0}{t1}<div className="mt-6">{t2}{t4}</div></div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
type Subscribed = {
  ok: true;
  message: string;
} | {
  ok: false;
  message: string;
  reason: string | undefined;
};

// Resolves, never rejects: a network failure comes back as a result.
async function subscribe(email: string): Promise<Subscribed> {
  try {
    const response = await fetch('/api/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email
      })
    });
    if (!response.ok) {
      // The route reports why in `error`, but an upstream 502 returns HTML —
      // so the body is parsed defensively.
      const failure: {
        error?: string;
      } = await response.json().catch(() => ({}));
      return {
        ok: false,
        message: failure.error || 'Something went wrong',
        reason: failure.error
      };
    }
    const data: {
      message?: string;
    } = await response.json().catch(() => ({}));
    return {
      ok: true,
      message: data.message || 'Successfully subscribed!'
    };
  } catch (error) {
    return {
      ok: false,
      message: 'Failed to connect. Please try again.',
      reason: error instanceof Error ? error.message : 'Failed to connect'
    };
  }
}

// The success UI waits for the input's vanish animation as well as the API,
// in either order: `vanished` notes an animation that finished first, and
// `revealed` is the success shown.
type Status = {
  s: 'idle';
} | {
  s: 'loading';
  vanished: boolean;
} | {
  s: 'success';
  message: string;
  revealed: boolean;
} | {
  s: 'error';
  message: string;
};
function useNewsletterForm() {
  const $ = _c(13);
  const [email, setEmail] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      s: "idle"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [status, setStatus] = useState(t0);
  const {
    track
  } = useAnalytics();
  const haptic = useWebHaptics();
  let t1;
  if ($[1] !== status.s) {
    t1 = e => {
      setEmail(e.target.value);
      if (status.s !== "idle") {
        setStatus({
          s: "idle"
        });
      }
    };
    $[1] = status.s;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const updateNewsletterEmail = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setStatus(_temp);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleAnimationComplete = t2;
  let t3;
  if ($[4] !== email || $[5] !== haptic || $[6] !== track) {
    t3 = e_0 => {
      e_0.preventDefault();
      if (!email) {
        return;
      }
      setStatus({
        s: "loading",
        vanished: false
      });
      subscribe(email).then(result => {
        if (result.ok) {
          track("newsletter_signup", {
            success: true
          });
          haptic.trigger("success");
          setStatus(prev_0 => ({
            s: "success",
            message: result.message,
            revealed: prev_0.s === "loading" && prev_0.vanished || prev_0.s === "success" && prev_0.revealed
          }));
          setEmail("");
          return;
        }
        track("newsletter_signup", {
          success: false,
          error: result.reason
        });
        haptic.trigger("error");
        setStatus({
          s: "error",
          message: result.message
        });
      });
    };
    $[4] = email;
    $[5] = haptic;
    $[6] = track;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleSubmit = t3;
  const t4 = status.s === "success" && status.revealed;
  let t5;
  if ($[8] !== handleSubmit || $[9] !== status || $[10] !== t4 || $[11] !== updateNewsletterEmail) {
    t5 = {
      status,
      showSuccess: t4,
      updateNewsletterEmail,
      handleAnimationComplete,
      handleSubmit
    };
    $[8] = handleSubmit;
    $[9] = status;
    $[10] = t4;
    $[11] = updateNewsletterEmail;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function _temp(prev) {
  if (prev.s === "loading") {
    return {
      ...prev,
      vanished: true
    };
  }
  if (prev.s === "success") {
    return {
      ...prev,
      revealed: true
    };
  }
  return prev;
}